/**
 * MessageShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { Card, Button, Space, Alert, notification, Modal, message, Result, Typography } from 'antd'
import {
  SmileOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

const cardBodyStyle = { padding: 10, height: 'calc(100% - 38px)', overflow: 'auto' as const }

/**
 * ============================================================================
 * MessageShowcase — Message/Notification/Modal/Alert/Result 카탈로그
 * ============================================================================
 *
 * 【역할】 사용자 피드백(toast/notification/modal/inline alert/result page) 을
 *         한 화면에 모아 트리거 버튼으로 즉시 비교.
 *
 * 【시연 항목】
 *   - Alert        — info/success/warning/error (banner 포함)
 *   - Message      — info/success/warning/error/loading (toast)
 *   - Notification — 4가지 type + 4가지 placement (top/bottom × left/right)
 *   - Modal        — useModal() imperative confirm/info/success/warning/error
 *   - Result       — success / 404 / custom (icon)
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/message' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd message/notification/Modal/Alert/Result/Button/Space/Typography
 * 【참고】 https://ant.design/components/message  /  /modal#Modal.useModal
 */
export default function MessageShowcase() {
  const { t } = useLabel()
  const [msg, msgCtx] = message.useMessage()
  const [noti, notiCtx] = notification.useNotification()
  const [mod, modCtx] = Modal.useModal()

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      {msgCtx}
      {notiCtx}
      {modCtx}

      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_MESSAGE_FEEDBACK_COMPONENTS', 'Message / Feedback 컴포넌트')}
        </Title>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gap: 10,
          overflow: 'hidden'
        }}
      >
        <Card
          size="small"
          title="Alert"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Alert type="info" message="Info alert" showIcon />
            <Alert type="success" message="Success alert" showIcon closable />
            <Alert type="warning" message="Warning alert" showIcon />
            <Alert type="error" message="Error alert" showIcon banner />
          </Space>
        </Card>

        <Card
          size="small"
          title="Message (toast)"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space wrap size={[4, 4]}>
            <Button size="small" icon={<InfoCircleOutlined />} onClick={() => msg.info('정보')}>
              Info
            </Button>
            <Button size="small" icon={<CheckCircleOutlined />} onClick={() => msg.success('성공')}>
              Success
            </Button>
            <Button size="small" icon={<WarningOutlined />} onClick={() => msg.warning('경고')}>
              Warning
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => msg.error('에러')}
            >
              Error
            </Button>
            <Button size="small" onClick={() => msg.loading('로딩...', 2)}>
              Loading
            </Button>
          </Space>
        </Card>

        <Card
          size="small"
          title="Notification"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space wrap size={[4, 4]}>
            <Button
              size="small"
              onClick={() => noti.info({ message: '알림', description: '우측 상단' })}
            >
              Info
            </Button>
            <Button
              size="small"
              onClick={() => noti.success({ message: '완료', description: '작업 완료' })}
            >
              Success
            </Button>
            <Button
              size="small"
              onClick={() => noti.warning({ message: '주의', description: '확인 필요' })}
            >
              Warning
            </Button>
            <Button
              size="small"
              danger
              onClick={() => noti.error({ message: '실패', description: '작업 실패' })}
            >
              Error
            </Button>
          </Space>
          <div style={{ height: 6 }} />
          <Space wrap size={[4, 4]}>
            {(['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const).map((p) => (
              <Button
                size="small"
                key={p}
                onClick={() =>
                  noti.open({ message: p, description: `placement=${p}`, placement: p })
                }
              >
                {p}
              </Button>
            ))}
          </Space>
        </Card>

        <Card
          size="small"
          title="Modal"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space wrap size={[4, 4]}>
            <Button
              size="small"
              onClick={() => mod.confirm({ title: '삭제', content: '정말 삭제?' })}
            >
              confirm
            </Button>
            <Button
              size="small"
              onClick={() => mod.info({ title: '정보', content: '안내 메시지' })}
            >
              info
            </Button>
            <Button size="small" onClick={() => mod.success({ title: '성공', content: '완료됨' })}>
              success
            </Button>
            <Button
              size="small"
              onClick={() => mod.warning({ title: '경고', content: '확인 필요' })}
            >
              warning
            </Button>
            <Button
              size="small"
              danger
              onClick={() => mod.error({ title: '에러', content: '에러 발생' })}
            >
              error
            </Button>
          </Space>
        </Card>

        <Card
          size="small"
          title="Result — Success"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Result
            status="success"
            title="제출 완료"
            subTitle="문서 20260415-001"
            style={{ padding: 8 }}
            extra={
              <Space size={4}>
                <Button size="small" type="primary">
                  콘솔
                </Button>
                <Button size="small">재구매</Button>
              </Space>
            }
          />
        </Card>

        <Card
          size="small"
          title="Result — 404 / Custom"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Result
              status="404"
              title="404"
              subTitle="페이지 없음"
              style={{ padding: 4 }}
              extra={
                <Button size="small" type="primary">
                  홈으로
                </Button>
              }
            />
            <Result
              icon={<SmileOutlined />}
              title="좋은 하루!"
              style={{ padding: 4 }}
              extra={
                <Button size="small" type="primary">
                  확인
                </Button>
              }
            />
          </Space>
        </Card>
      </div>
    </PageContainer>
  )
}
